import React from 'react';
import { useWatch } from 'react-hook-form';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { FactorType } from '@/api/generated';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { OtpInput } from '@/components/ui/otp-input';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { useLanguage } from '@/contexts/language-context';
import { useOtpForm } from '@/hooks/forms/use-otp-form';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import { useMfaResend } from '@/hooks/use-mfa-resend';
import {
  useResendCountdown,
  useResendDeadline,
} from '@/hooks/use-resend-countdown';
import { resendOtp } from '@/services/apis/otp.api';

interface WhatsappOtpFactorProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  phoneNumberObfuscated?: string;
  nextResendAt?: string;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const WhatsappOtpFactor: React.FC<WhatsappOtpFactorProps> = ({
  submitResponse,
  phoneNumberObfuscated,
  nextResendAt,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();

  const {
    form,
    onSubmit,
    isSubmitting,
    getErrorMessage,
    hasExceededRetries,
    resetAttempts,
    handleCodeChange,
    handleCodePasted,
    handleComplete,
    handleResendRequested,
  } = useOtpForm({
    submitOtp: submitResponse,
    method: FactorType.WHATSAPP_OTP,
    maxRetries: 3,
  });

  const { deadline, setDeadline, isResolving } =
    useResendDeadline(nextResendAt);
  const { canResend, isExhausted, formattedTime } =
    useResendCountdown(deadline);

  const { resend, isResending } = useMfaResend({
    resendRequest: resendOtp,
    onResendSuccess: result => {
      setDeadline(result);
      resetAttempts();
    },
  });

  const handleResendClick = () => {
    void resend();
    handleResendRequested();
  };

  const { control, handleSubmit } = form;
  const codeValue = useWatch({ control, name: 'code' });

  const errorMessage = getErrorMessage();
  const hasError = Boolean(errorMessage);

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-1'>
            <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
              Verify using WhatsApp
            </Text>
            <Text size={TextSizes.SM} className='text-secondary'>
              {phoneNumberObfuscated
                ? `Enter the 6-digit code sent to ${phoneNumberObfuscated}`
                : 'Enter the 6-digit code sent via WhatsApp'}
            </Text>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='flex flex-col gap-4'>
              <OtpInput
                value={codeValue}
                onChange={handleCodeChange}
                onComplete={handleComplete}
                onCodePasted={handleCodePasted}
                hasError={hasError}
                disabled={isSubmitting || hasExceededRetries}
                autoFocus
                length={6}
              />

              {hasError && <StatusMessage>{errorMessage}</StatusMessage>}

              <LoginButton
                data-testid='submit-whatsapp-otp-button'
                type='submit'
                size={Sizes.LG}
                className='w-full'
                isLoading={isSubmitting}
                disabled={hasExceededRetries}
              >
                {isSubmitting ? t('auth.signingIn') : t('common.continue')}
              </LoginButton>

              <div className='flex flex-col gap-2 items-center mt-2'>
                <Text
                  as='p'
                  size={TextSizes.SM}
                  weight={FontWeights.MEDIUM}
                  className='text-center'
                >
                  {t('mfa.didntReceiveIt')}
                </Text>
                {!isResolving &&
                  (canResend ? (
                    <LoginButton
                      data-testid='otp-resend-button'
                      type='button'
                      variant={ButtonVariants.LINK_PRIMARY}
                      onClick={handleResendClick}
                      disabled={isResending}
                    >
                      {t('mfa.resendCode')}
                    </LoginButton>
                  ) : (
                    <Text
                      as='p'
                      size={TextSizes.SM}
                      className='text-secondary text-center'
                    >
                      {isExhausted
                        ? t('mfa.resendLimitReached')
                        : `${t('mfa.requestNewCodeIn')} ${formattedTime}`}
                    </Text>
                  ))}
              </div>
            </div>
          </form>
        </div>
      </LoginCard>
    </Page>
  );
};
